import { Component, ErrorInfo, ReactNode } from 'react';
import { RefreshCw, Home } from 'lucide-react';
import { Button } from '@heroui/react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
    }

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Update state with error info
    this.setState({
      error,
      errorInfo
    });

    // Here we could also log the error to an error reporting service
    // e.g., Sentry, LogRocket, etc.
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      // If custom fallback is provided, use it
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI - polished error page
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
          <div className="relative max-w-4xl w-full flex flex-col items-center text-center space-y-8">
            {/* Large 500 watermark */}
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-5">
              <span className="select-none text-[140px] md:text-[220px] font-black text-slate-900">
                500
              </span>
            </div>



            {/* Text content */}
            <div className="relative space-y-3 max-w-xl">
              <h1 className="text-3xl md:text-4xl font-black tracking-tight text-slate-900">
                Oops, something went wrong
              </h1>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                You can try
                refreshing the page, or go back to the dashboard and continue
                from there. If the problem persists please contact support.
              </p>
            </div>

            {/* Dev-only error details */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="relative w-full max-w-2xl text-left">
                <div className="mb-4 rounded-xl border border-rose-100 bg-rose-50/80 px-4 py-3">
                  <p className="text-xs font-semibold text-rose-700 mb-1">
                    Error details (development only)
                  </p>
                  <p className="text-[11px] text-rose-700 font-mono break-all">
                    {this.state.error.toString()}
                  </p>
                  {this.state.errorInfo && (
                    <details className="mt-2">
                      <summary className="text-[11px] text-rose-700 cursor-pointer">
                        Stack trace
                      </summary>
                      <pre className="mt-2 max-h-40 overflow-auto text-[11px] text-rose-700 font-mono whitespace-pre-wrap">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="relative flex flex-col sm:flex-row gap-3 w-full sm:w-auto justify-center">
              <Button
                onClick={this.handleReset}
                size='lg'
                className="flex rounded-full  items-center justify-center  bg-primary text-white shadow-sm hover:bg-primary/80 transition-colors"
              >
                <span>Refresh page</span>
              </Button>
              <Button
               variant='flat'
               size='lg'
                onClick={this.handleGoHome}
                className="flex items-center justify-center rounded-full text-sm  transition-colors"
              >
                <span>Go to dashboard</span>
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

